import {daysBetween, getISODate, isSameDay, Temporal} from "@/shared/utils/datefunctions";

type Endpoint = "start" | "end";

/** The exchanged value: pure data the parent seeds and reads back via `onValueChange`. */
export interface DateRangeValue {
	start: Temporal.PlainDate | null;
	end: Temporal.PlainDate | null;
	startHalfDay: boolean;
	endHalfDay: boolean;
}

export function cloneDateRangeValue<T extends DateRangeValue>(value: T): T {
	// Temporal.PlainDate is immutable, so a shallow copy is a full clone.
	return {...value};
}

/** Normalizes the half-day flags in place: only meaningful in half-day mode, and never on a single-day range. */
function normalizeHalfDays<T extends DateRangeValue>(value: T, halfDayMode: boolean): T {
	if (!halfDayMode) {
		value.startHalfDay = false;
		value.endHalfDay = false;
	} else if (isSameDay(value.start, value.end)) {
		value.endHalfDay = false;
	}
	return value;
}

/**
 * Applies a date edit: moving the start shifts the end by the current day span (preserving width),
 * while moving the end just sets it (re-widening the span).
 */
export function applyDateChange(value: DateRangeValue, endpoint: Endpoint, date: Temporal.PlainDate | null, halfDayMode: boolean): DateRangeValue {
	const next = cloneDateRangeValue(value);
	if (endpoint === "start") {
		const days = Math.max(0, daysBetween(value.start, value.end));
		next.start = date ?? null;
		next.end = date ? date.add({days}) : null;
	} else {
		next.end = date ?? null;
	}
	return normalizeHalfDays(next, halfDayMode);
}

export function applyHalfDayChange(value: DateRangeValue, endpoint: Endpoint, halfDay: boolean, halfDayMode: boolean): DateRangeValue {
	const next = cloneDateRangeValue(value);
	next[endpoint === "start" ? "startHalfDay" : "endHalfDay"] = halfDay;
	return normalizeHalfDays(next, halfDayMode);
}

export function isSingleDayHalfDayRange(value: DateRangeValue, halfDayMode: boolean): boolean {
	return halfDayMode && isSameDay(value.start, value.end);
}

export function shouldShowEndHalfDay(value: DateRangeValue, halfDayMode: boolean): boolean {
	return halfDayMode && !isSingleDayHalfDayRange(value, halfDayMode);
}

export function formatDateRangeHidden(value: DateRangeValue, endpoint: Endpoint): string {
	return getISODate(value[endpoint]);
}
